import { useCallback, useMemo } from 'react';
import { cn } from '../../utils/cn';
import styles from './my-coupon-card.module.css';
import type { MyCouponCardProps } from './my-coupon-card.types';
import { COUPON_CARD_VARIANT } from './my-coupon-card.constants';
import { useFormatDate } from '../../utils/use-format-date';

type UseMyCouponCardParams = Pick<
    MyCouponCardProps,
    | 'coupon'
    | 'variant'
    | 'className'
    | 'titleProps'
    | 'messageProps'
    | 'salesRuleProps'
    | 'progressSectionProps'
    | 'progressTrackProps'
    | 'progressFillProps'
    | 'isAdding'
>;

export const useMyCouponCard = ({
    coupon,
    variant = 'default',
    className,
    titleProps,
    messageProps,
    salesRuleProps,
    progressSectionProps,
    progressTrackProps,
    progressFillProps,
    isAdding = false,
}: UseMyCouponCardParams) => {
    const { name, tag, salesrule, imageUri, type } = coupon;

    const isDetail = variant === COUPON_CARD_VARIANT.DETAIL;

    const progressValue = useMemo(() => {
        if (!salesrule?.usage_limit || !salesrule?.times_used) return 0;
        return Math.round((salesrule.times_used / salesrule.usage_limit) * 100);
    }, [salesrule]);
    const { formatDate: baseFormatDate } = useFormatDate();
    const formatDate = useCallback(
        (dateString?: string) => {
            const formatType = isDetail ? 'dot' : 'full';
            return baseFormatDate(dateString, formatType);
        },
        [isDetail, baseFormatDate]
    );
    const progressClassName = useMemo(() => {
        if (progressValue >= 80) {
            return isDetail ? styles.detailProgressError : styles.defaultProgressError;
        }

        if (progressValue >= 50) {
            return isDetail ? styles.detailProgressWarning : styles.defaultProgressWarning;
        }

        return isDetail ? styles.detailProgressSuccess : styles.defaultProgressSuccess;
    }, [isDetail, progressValue]);

    const rootClassName = useMemo(
        () => cn(isDetail ? styles.detailRoot : styles.defaultRoot, className),
        [isDetail, className]
    );

    const contentClassName = isDetail ? styles.detailContent : styles.defaultContent;
    const imageWrapperClassName = isDetail ? styles.detailImageWrapper : styles.defaultImageWrapper;
    const imageClassName = isDetail ? styles.detailImage : styles.defaultImage;
    const detailsClassName = isDetail ? styles.detailDetails : styles.defaultDetails;
    const contentBottomClassName = isDetail ? styles.detailContentBottom : styles.defaultContentBottom;
    const progressLabelClassName = isDetail ? styles.detailProgressLabel : styles.defaultProgressLabel;

    const titleClassName = useMemo(
        () => cn(isDetail ? styles.detailTitle : styles.defaultTitle, titleProps?.className),
        [isDetail, titleProps?.className]
    );

    const messageClassName = useMemo(
        () => cn(isDetail ? styles.detailMessage : styles.defaultMessage, messageProps?.className),
        [isDetail, messageProps?.className]
    );

    const salesRuleClassName = useMemo(
        () => cn(isDetail ? styles.detailSalesRule : styles.defaultSalesRule, salesRuleProps?.className),
        [isDetail, salesRuleProps?.className]
    );

    const progressSectionClassName = useMemo(
        () =>
            cn(
                isDetail ? styles.detailProgressSection : styles.defaultProgressSection,
                progressSectionProps?.className
            ),
        [isDetail, progressSectionProps?.className]
    );

    const progressTrackClassName = useMemo(
        () => cn(isDetail ? styles.detailProgressTrack : styles.defaultProgressTrack, progressTrackProps?.className),
        [isDetail, progressTrackProps?.className]
    );

    const progressFillClassName = useMemo(
        () =>
            cn(
                isDetail ? styles.detailProgressFill : styles.defaultProgressFill,
                progressClassName,
                progressFillProps?.className
            ),
        [isDetail, progressClassName, progressFillProps?.className]
    );

    const getCodeButtonClassName = useMemo(
        () => cn(styles.defaultGetCodeButton, isAdding && styles.loadingGetCodeButton),
        [isAdding]
    );

    const typeClassName = useMemo(
        () => cn(isDetail ? styles.detailType : styles.defaultType, messageProps?.className),
        [isDetail, messageProps?.className]
    );

    return {
        name,
        type,
        tag,
        salesrule,
        imageUri,
        isDetail,
        progressValue,
        formatDate,
        rootClassName,
        contentClassName,
        imageWrapperClassName,
        imageClassName,
        detailsClassName,
        contentBottomClassName,
        titleClassName,
        messageClassName,
        salesRuleClassName,
        progressSectionClassName,
        progressTrackClassName,
        progressFillClassName,
        progressLabelClassName,
        getCodeButtonClassName,
        typeClassName,
    };
};
